import { jsx, jsxs } from "react/jsx-runtime";
import { createContext, useState, useReducer, useContext, useRef, useEffect } from "react";
import FocusLock from "react-focus-lock";
import { Box } from "../../Box/Box.mjs";
import { BraidPortal } from "../../BraidPortal/BraidPortal.mjs";
import { ModalContent } from "./ModalContent.mjs";
import { externalGutter } from "./ModalExternalGutter.mjs";
import { ariaHideOthers } from "./ariaHideOthers.mjs";
import { resetStackingContext, backdrop, transition, modalContainer, entrance, exit, fixedStackingContext } from "./Modal.css.mjs";
const AllowCloseContext = createContext(true);
const ModalPortal = ({ children }) => {
  const [modalElement, setElement] = useState(null);
  useEffect(() => {
    const modalContainerId = "braid-modal-container";
    let element = document.getElementById(modalContainerId);
    if (!element) {
      element = document.createElement("div");
      element.setAttribute("id", modalContainerId);
      element.setAttribute("class", fixedStackingContext);
      document.body.appendChild(element);
    }
    setElement(element);
  }, []);
  if (!modalElement) {
    return null;
  }
  return /* @__PURE__ */ jsx(BraidPortal, { container: modalElement, children });
};
const OPEN_MODAL = 1;
const CLOSE_MODAL = 2;
const ANIMATION_COMPLETE = 3;
const INITIAL = 1;
const OPEN = 2;
const OPENING = 3;
const CLOSED = 4;
const CLOSING = 5;
const reducer = (prevState, action) => {
  switch (action) {
    case OPEN_MODAL: {
      switch (prevState) {
        case INITIAL:
        case CLOSING:
        case CLOSED: {
          return OPENING;
        }
      }
      return prevState;
    }
    case CLOSE_MODAL: {
      switch (prevState) {
        case OPEN:
        case OPENING: {
          return CLOSING;
        }
      }
      return prevState;
    }
    case ANIMATION_COMPLETE: {
      switch (prevState) {
        case CLOSING: {
          return CLOSED;
        }
        case OPENING: {
          return OPEN;
        }
      }
      return prevState;
    }
    default: {
      return prevState;
    }
  }
};
const ANIMATION_DURATION = 300;
const Modal = ({
  id,
  open,
  children,
  description,
  onClose,
  width,
  closeLabel,
  illustration,
  title,
  headingLevel,
  position,
  ...restProps
}) => {
  const [trapActive, setTrapActive] = useState(true);
  const [state, dispatch] = useReducer(reducer, INITIAL);
  const allowClose = useContext(AllowCloseContext);
  const shouldFocus = typeof document !== "undefined" && typeof document.hasFocus === "function" && document.hasFocus();
  const openRef = useRef(open);
  const modalRef = useRef(null);
  const headingRef = useRef(null);
  const closeHandlerRef = useRef(onClose);
  const initiateClose = () => {
    if (allowClose) {
      const result = closeHandlerRef.current(false);
      if (result === false) {
        return result;
      }
      if (openRef.current) {
        dispatch(CLOSE_MODAL);
      }
    }
  };
  useEffect(() => {
    openRef.current = open;
    dispatch(open ? OPEN_MODAL : CLOSE_MODAL);
  }, [open]);
  const closing = state === CLOSING;
  useEffect(() => {
    if (closing) {
      const timer = setTimeout(() => {
        dispatch(ANIMATION_COMPLETE);
      }, ANIMATION_DURATION);
      return () => clearTimeout(timer);
    }
  }, [closing]);
  const shouldAriaHideOthers = state === OPEN || state === CLOSING;
  useEffect(() => {
    if (shouldAriaHideOthers && modalRef.current) {
      return ariaHideOthers(modalRef.current, { delay: ANIMATION_DURATION });
    }
  }, [shouldAriaHideOthers]);
  useEffect(() => {
    if (typeof onClose === "function") {
      closeHandlerRef.current = onClose;
    }
  }, [onClose]);
  useEffect(() => {
    const event = trapActive ? "blur" : "focus";
    const handleEvent = () => setTrapActive(!trapActive);
    window.addEventListener(event, handleEvent);
    return () => {
      window.removeEventListener(event, handleEvent);
    };
  }, [trapActive]);
  return state === OPENING || state === OPEN || state === CLOSING ? /* @__PURE__ */ jsx(ModalPortal, { children: /* @__PURE__ */ jsxs(
    FocusLock,
    {
      className: resetStackingContext,
      disabled: !trapActive,
      autoFocus: false,
      onActivation: () => {
        if (state === OPEN) {
          return;
        }
        if (headingRef.current && shouldFocus) {
          headingRef.current.focus();
        }
        dispatch(ANIMATION_COMPLETE);
      },
      returnFocus: true,
      children: [
        /* @__PURE__ */ jsx(
          Box,
          {
            onClick: state === OPEN ? initiateClose : void 0,
            position: "fixed",
            inset: 0,
            zIndex: "modalBackdrop",
            opacity: state !== OPEN ? 0 : void 0,
            pointerEvents: state === CLOSING ? "none" : void 0,
            className: [backdrop, transition[position]]
          }
        ),
        /* @__PURE__ */ jsx(
          Box,
          {
            position: "fixed",
            inset: 0,
            zIndex: "modal",
            pointerEvents: "none",
            opacity: state !== OPEN ? 0 : void 0,
            paddingLeft: position === "right" ? ["none", "xlarge"] : void 0,
            paddingRight: position === "left" ? ["none", "xlarge"] : void 0,
            padding: position === "center" ? externalGutter : void 0,
            className: [
              modalContainer,
              transition[position],
              state === OPENING && entrance[position],
              state === CLOSING && exit[position]
            ],
            children: /* @__PURE__ */ jsx(
              ModalContent,
              {
                id,
                description,
                onClose: initiateClose,
                width,
                closeLabel,
                illustration,
                title,
                headingLevel,
                headingRef,
                modalRef,
                position,
                scrollLock: !(state === CLOSING),
                ...restProps,
                children
              }
            )
          }
        )
      ]
    }
  ) }) : null;
};
export {
  AllowCloseContext,
  Modal
};
